// Pure math for the F-V operating-point dot.
//
// The dot rides a damper's bench F-V curve at the instantaneous damper
// velocity under the shared cursor. Velocity is not a channel we receive; it is
// differentiated client-side from the predicted damper-position trace
// (waveform_json, metres) against its time axis (seconds), giving m/s, then
// converted to in/s to match the dyno-plot convention. The curve arrives in SI
// (m/s, N); consumers plot it as in/s vs lbf, so the force lookup returns lbf.

// 1 m/s = 39.37 in/s. 1 N = 0.2248 lbf. Same constants the edit-sheet curve
// preview uses, kept here so both the axis conversion and the dot agree.
export const MPS_TO_INPS = 39.37007874015748;
export const N_TO_LBF = 0.2248089430997105;

// Central-difference derivative of y with respect to t on a (possibly
// non-uniform) grid. Interior points use the symmetric two-sided difference;
// the endpoints fall back to a one-sided (forward / backward) difference.
// Units follow the inputs: position in metres over time in seconds yields m/s.
//
//   interior i : (y[i+1] - y[i-1]) / (t[i+1] - t[i-1])
//   first  i=0 : (y[1]   - y[0]  ) / (t[1]   - t[0]  )
//   last   i=n : (y[n]   - y[n-1]) / (t[n]   - t[n-1])
//
// Returns [] for an empty input and [0] for a single sample (no rate defined).
// A zero time-step at any position yields 0 there rather than a non-finite.
export function centralDifference(y: number[], t: number[]): number[] {
  const n = y.length;
  if (n !== t.length) {
    throw new Error(
      `centralDifference: length mismatch (y=${n}, t=${t.length})`,
    );
  }
  if (n === 0) return [];
  if (n === 1) return [0];
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    let dy: number;
    let dt: number;
    if (i === 0) {
      dy = y[1] - y[0];
      dt = t[1] - t[0];
    } else if (i === n - 1) {
      dy = y[n - 1] - y[n - 2];
      dt = t[n - 1] - t[n - 2];
    } else {
      dy = y[i + 1] - y[i - 1];
      dt = t[i + 1] - t[i - 1];
    }
    out[i] = dt === 0 ? 0 : dy / dt;
  }
  return out;
}

// Convert an m/s velocity to in/s.
export function mpsToInPs(v: number): number {
  return v * MPS_TO_INPS;
}

// A bench curve as stored on the wire (SI). Kept structural so callers can pass
// the Zod-parsed DamperCurve directly.
export type SiCurve = { velocity_mps: number[]; force_N: number[] };

// The same curve in display coordinates: velocity in/s, force lbf.
export type DisplayCurve = { velInPs: number[]; forceLbf: number[] };

export function toDisplayCurve(curve: SiCurve): DisplayCurve {
  return {
    velInPs: curve.velocity_mps.map((v) => v * MPS_TO_INPS),
    forceLbf: curve.force_N.map((f) => f * N_TO_LBF),
  };
}

// Linear interpolation of ys at x over a monotonic-ascending xs. Returns null
// when x is outside [xs[0], xs[last]] (the dot hides rather than extrapolating)
// or when the curve is degenerate.
export function interpLinear(
  xs: number[],
  ys: number[],
  x: number,
): number | null {
  const n = xs.length;
  if (n === 0 || n !== ys.length) return null;
  if (!Number.isFinite(x)) return null;
  if (x < xs[0] || x > xs[n - 1]) return null;
  let i = 1;
  while (i < n && xs[i] < x) i++;
  if (i >= n) return ys[n - 1];
  const x0 = xs[i - 1];
  const x1 = xs[i];
  const y0 = ys[i - 1];
  const y1 = ys[i];
  if (x1 === x0) return y0;
  return y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
}

export type OperatingPoint = { velInPs: number; forceLbf: number };

// The dot for one corner at one cursor index: the damper velocity there (in/s)
// and the force the curve carries at that velocity (lbf). Returns null when the
// index is out of range or the velocity falls outside the curve's velocity
// span (nothing to draw).
export function operatingPoint(
  velocityMps: number[],
  curve: DisplayCurve,
  idx: number | null,
): OperatingPoint | null {
  if (idx === null || idx < 0 || idx >= velocityMps.length) return null;
  const velInPs = velocityMps[idx] * MPS_TO_INPS;
  if (!Number.isFinite(velInPs)) return null;
  const forceLbf = interpLinear(curve.velInPs, curve.forceLbf, velInPs);
  if (forceLbf === null) return null;
  return { velInPs, forceLbf };
}
